import { getProductionEquipmentCategory } from './productionEquipmentCategories'

const moneyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
})

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'America/New_York',
})

const serviceOrder = [
  'Sound',
  'Visuals',
  'Lighting',
  'Staging',
  'DJ Equipment',
  'Special Effects',
  'Live Streaming',
  'Production Support',
]

const serviceMatchers = [
  ['Sound', /\b(audio|sound|speaker|microphone|wireless mic|mixer)\b/i],
  ['Visuals', /\b(video|visual|projector|screen|display|led wall|television|tv)\b/i],
  ['Lighting', /\b(light|lighting|uplight|spotlight|moving head)\b/i],
  ['Staging', /\b(stage|staging|truss|pipe and drape|tent)\b/i],
  ['DJ Equipment', /\b(dj|cdj|turntable|controller)\b/i],
  ['Special Effects', /\b(special effect|cold spark|co2|fog|haze)\b/i],
  ['Live Streaming', /\b(stream|broadcast|hybrid|virtual event)\b/i],
]

const categoryPurposes = {
  Sound: 'Speech, music and room coverage',
  Visuals: 'Presentation, playback and audience visibility',
  Lighting: 'Stage and room illumination',
  Staging: 'Presentation and performance structure',
  'DJ Equipment': 'Playback, mixing and live performance',
  'Special Effects': 'Atmosphere and audience moments',
  Other: 'Power, signal and production infrastructure',
}

const toFinitePositiveNumber = value => {
  const number = Number(value)
  return Number.isFinite(number) && number > 0 ? number : null
}

const titleCase = value => String(value || '')
  .replace(/[-_]+/g, ' ')
  .replace(/\b\w/g, character => character.toUpperCase())
  .trim()

export const formatPortfolioMoney = value => {
  const amount = toFinitePositiveNumber(value)
  return amount === null ? '' : moneyFormatter.format(amount)
}

export const formatPortfolioDate = value => {
  const date = new Date(value)
  return Number.isFinite(date.getTime()) ? dateFormatter.format(date) : ''
}

export const getProjectInvestment = event => {
  const price = event?.order?.price || {}
  const equipment = toFinitePositiveNumber(price.rental)
  const labor = toFinitePositiveNumber(price.production)
  const orderTotal = toFinitePositiveNumber(price.total)
  const hasVerifiedSplit = equipment !== null && labor !== null
  const total = orderTotal ?? (hasVerifiedSplit ? equipment + labor : null)

  return {
    equipment,
    labor,
    total,
    hasVerifiedSplit,
    hasVerifiedTotal: total !== null,
  }
}

export const getBudgetBand = event => {
  const total = getProjectInvestment(event).total
  if (total === null) return 'scope-dependent'
  if (total < 5000) return 'under-5'
  if (total < 15000) return '5-15'
  if (total < 30000) return '15-30'
  return '30-plus'
}

export const getEventType = event => titleCase(event?.category) || 'Event Production'

export const getEventVenue = event => {
  const directVenue = event?.venue || event?.location?.name
  return directVenue ? String(directVenue).trim() : ''
}

export const getEventServices = (event, products = []) => {
  const productServices = products
    .map(getProductionEquipmentCategory)
    .filter(category => category && category !== 'Other')
  const searchable = [
    event?.title,
    event?.subtitle,
    event?.main_paragraph,
    event?.second_paragraph,
    event?.category,
    ...(event?.tags || []),
  ].filter(Boolean).join(' ')
  const textServices = serviceMatchers
    .filter(([, matcher]) => matcher.test(searchable))
    .map(([service]) => service)
  const services = [...new Set([...productServices, ...textServices])]

  if (!services.length) services.push('Production Support')

  return services.sort((left, right) => {
    const leftIndex = serviceOrder.indexOf(left)
    const rightIndex = serviceOrder.indexOf(right)
    return (leftIndex < 0 ? serviceOrder.length : leftIndex) - (rightIndex < 0 ? serviceOrder.length : rightIndex)
  })
}

export const getEventSearchText = event => [
  event?.title,
  event?.subtitle,
  event?.main_paragraph,
  event?.second_paragraph,
  event?.category,
  ...(event?.tags || []),
  getEventVenue(event),
  ...getEventServices(event),
].filter(Boolean).join(' ').toLowerCase()

export const getEquipmentManifest = event => {
  const products = (event?.products || []).filter(product => product?.status === 'active')
  const lineItems = event?.order?.rental?.items || []

  return products.map(product => {
    const lineItem = lineItems.find(item => {
      const productId = item?.product?._id || item?.product
      return String(productId || '') === String(product?._id || '')
    })
    const quantity = toFinitePositiveNumber(lineItem?.units)
      || toFinitePositiveNumber(lineItem?.requested)
      || toFinitePositiveNumber(lineItem?.item_count)
      || (Array.isArray(lineItem?.items) && lineItem.items.length ? lineItem.items.length : 1)
    const category = getProductionEquipmentCategory(product)

    return {
      category,
      product,
      quantity,
    }
  }).sort((left, right) => {
    const leftIndex = serviceOrder.indexOf(left.category)
    const rightIndex = serviceOrder.indexOf(right.category)
    const categoryDifference = (leftIndex < 0 ? serviceOrder.length : leftIndex)
      - (rightIndex < 0 ? serviceOrder.length : rightIndex)
    if (categoryDifference) return categoryDifference
    const priceDifference = (toFinitePositiveNumber(right.product?.price?.rental) || 0)
      - (toFinitePositiveNumber(left.product?.price?.rental) || 0)
    if (priceDifference) return priceDifference
    return right.quantity - left.quantity || String(left.product?.name || '').localeCompare(String(right.product?.name || ''))
  })
}

export const getEquipmentGroups = manifest => Object.values(manifest.reduce((groups, item) => {
  if (!groups[item.category]) {
    groups[item.category] = {
      category: item.category,
      itemCount: 0,
      purpose: categoryPurposes[item.category] || categoryPurposes.Other,
      uniqueProducts: 0,
    }
  }
  groups[item.category].itemCount += item.quantity
  groups[item.category].uniqueProducts += 1
  return groups
}, {}))

export const getPortfolioFilterOptions = events => {
  const eventTypes = [...new Set((events || []).map(getEventType).filter(Boolean))].sort()
  const services = [...new Set((events || []).flatMap(event => getEventServices(event)))].sort((left, right) => {
    const leftIndex = serviceOrder.indexOf(left)
    const rightIndex = serviceOrder.indexOf(right)
    return (leftIndex < 0 ? serviceOrder.length : leftIndex) - (rightIndex < 0 ? serviceOrder.length : rightIndex)
  })

  return { eventTypes, services }
}
